// ====================================================================
// ziven-theme-aura v0.2.8 modifySidebar.js
// ====================================================================
//
// 桌面端 sidebar 注入 (>= 992px), 跟 ziven.ski 1.8 真实 1:1
// 移动端 (< 992px) 走 modifyDrawer.js (drawer 模式), sidebar 被 CSS 隐藏
//
// ziven.ski 桌面端真实结构 (Playwright 调研):
//   - .App-sidebar 容器: width 280px, height 848px, top 52, left 0
//   - background: rgb(25, 25, 25) = #191919 (深色)
//   - position: fixed
//   - 内部: user-block + 5 stats + nav section (5 主体 + separator + 3 账号)
//   - 折叠按钮 < 在 sidebar 右边缘, 垂直居中 (圆点按钮)
//
// v0.2.0 实施 (辉哥 13:34 拍板方案 A 完整复刻):
//   - 桌面端保留 vendor top bar (在 .App-drawer 内部, ziven.ski 风格 logo + 搜索 + 通知 + 头像)
//   - 桌面端隐藏 ziven-theme-aura 注入的 user-block + 5 stats + nav (在 .App-drawer 内部), 让 sidebar 接管
//   - 注入 .App-sidebar-aura (280px 宽, fixed, top 52, left 0, z-index 1000)
//   - 加折叠按钮 .App-sidebar-aura-toggle (right 0, 垂直居中, 圆形 < 按钮)
//   - App-content margin-left: 280px (展开) / 0 (折叠)
//   - 折叠状态持久化到 localStorage 'ziven-aura-sidebar-collapsed'
//   - 监听 ziven-aura-viewport-changed 事件 (从 modifyDrawer.js 触发, viewport 切换时重新评估)
//
// 跟 ziven.ski 真实色板 1:1:
//   - sidebar 背景 #191919
//   - user-block 边框 #2a3346
//   - 文字 h4 纯白 / 等级 蓝灰 / stat 数字 蓝灰 / stat 标签 纯白
//   - accent 蓝 #426799
// ====================================================================

import app from 'flarum/common/app';
import { extend } from 'flarum/common/extend';
import { buildUserBlock, buildStatsSection, buildNavSection, renderNavItems } from './buildElements';
const STORAGE_KEY = 'ziven-aura-sidebar-collapsed';

// === 注入 desktop sidebar ===
function injectDesktopSidebar() {
  const user = app.session.user;
  if (!user) return;

  // 桌面端才注入
  if (!window.matchMedia('(min-width: 992px)').matches) {
    // 移动端, 移除 sidebar
    const existing = document.querySelector('.App-sidebar-aura');
    if (existing) existing.remove();
    return;
  }

  // 已经存在, 不重复注入
  if (document.querySelector('.App-sidebar-aura')) return;
  const appEl = document.getElementById('app');
  if (!appEl) return;

  // === 1. 创建 sidebar 容器 ===
  const sidebar = document.createElement('aside');
  sidebar.className = 'App-sidebar-aura';

  // 应用持久化折叠状态
  const isCollapsed = localStorage.getItem(STORAGE_KEY) === '1';
  if (isCollapsed) {
    document.body.classList.add('App-sidebar-aura-collapsed');
  }

  // 1.1 user-block (居中堆叠)
  const userBlock = buildUserBlock(user);
  sidebar.appendChild(userBlock);

  // 1.2 5 stats 横向 (v0.2.6: 传 user, 改读真实数据)
  const statsSection = buildStatsSection(user);
  sidebar.appendChild(statsSection);

  // 1.3 nav section (8 nav: 5 主体 + 1 separator + 3 账号)
  const navSection = buildNavSection();
  sidebar.appendChild(navSection);

  // === 2. 注入到 #app 第一个子元素之前 (跟 vendor drawer 同级) ===
  if (appEl.firstChild) {
    appEl.insertBefore(sidebar, appEl.firstChild);
  } else {
    appEl.appendChild(sidebar);
  }

  // === 3. 折叠按钮 (作为 #app 直接子元素, 不作为 sidebar 子元素, bug 1 修复) ===
  // bug 1 根因: CSS Transforms Spec Level 1, position: fixed 元素的 ancestor 有 transform 时,
  //   fixed 元素相对那个 ancestor 定位, 不相对 viewport. sidebar transform: translateX(-100%)
  //   会让 toggle 跟着 sidebar 移动到 viewport 外, 折叠后不可见
  // 修法: toggle 作为 #app 直接子元素, 不作为 sidebar 子元素, toggle 单独 fixed 相对 viewport
  const toggle = document.createElement('button');
  toggle.className = 'App-sidebar-aura-toggle';
  toggle.setAttribute('aria-label', '折叠侧边栏');
  toggle.innerHTML = isCollapsed ? '<i class="fas fa-chevron-right"></i>' : '<i class="fas fa-chevron-left"></i>';
  toggle.addEventListener('click', toggleSidebar);
  // 注入到 sidebar 之后 (跟 sidebar 平级)
  if (sidebar.nextSibling) {
    appEl.insertBefore(toggle, sidebar.nextSibling);
  } else {
    appEl.appendChild(toggle);
  }
}

// === 折叠/展开 sidebar ===
function toggleSidebar() {
  const collapsed = document.body.classList.toggle('App-sidebar-aura-collapsed');
  localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  // 更新 toggle 图标
  const toggle = document.querySelector('.App-sidebar-aura-toggle');
  if (toggle) {
    toggle.innerHTML = collapsed ? '<i class="fas fa-chevron-right"></i>' : '<i class="fas fa-chevron-left"></i>';
  }
}

// === v0.2.8: 路由切换时重新 render nav items (桌面端 sidebar v0.2.4 之后 CSS hidden, 但代码保留) ===
// v0.2.8 修: 传 currentPath (m.route.get() 在异步路由切换期间可能返回旧值)
function refreshNavActive() {
  setTimeout(() => {
    const sidebar = document.querySelector('.App-sidebar-aura');
    if (!sidebar) return;
    const navSection = sidebar.querySelector('.App-drawer-aura-nav');
    if (!navSection) return;
    try {
      renderNavItems(navSection, window.__ziven_aura_current_path__);
    } catch (e) {
      console.error('[ziven-theme-aura] refreshNavActive (desktop) failed:', e);
    }
  }, 0);
}

// === v0.2.5: monkey-patch history.pushState/replaceState 监听路由切换 ===
// 跟 modifyDrawer.js 共享 `__ziven_aura_patched_v3__` 标记, 只 patch 一次
// mithril 路由内部用 history.pushState 跳转, popstate 不能触发, 需要 monkey-patch
// v0.2.8 修 (重要, SOP 133 候选): chain pattern + 同步缓存 currentPath
//   - 之前用 `window.__ziven_aura_patched__` guard, 但被 Playwright 客户端先 patch 占用
//   - ziven-theme-aura 的 listener 永远不被调用, 路由切换时 active 不更新
//   - 修法: 改 guard name `__ziven_aura_patched_v3__` + chain pattern
//     找到链头 `currentPush.__ziven_aura_original_v3__ || currentPush`,
//     不会覆盖 Playwright 客户端的 patch, 链头永远指向原生 pushState
//   - 同时在 pushState 调用的瞬间, 同步缓存 currentPath 到 window.__ziven_aura_current_path__
//     (因为 mithril 0.2.x 路由切换是异步的, m.render 同步调用时 m.route.get() 仍返回旧值)
function patchHistory() {
  if (window.__ziven_aura_patched_v3__) return;
  window.__ziven_aura_patched_v3__ = true;
  const currentPush = history.pushState;
  const originalPush = currentPush.__ziven_aura_original_v3__ || currentPush;
  const wrappedPush = function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    originalPush.apply(this, args);
    window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
    window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
  };
  wrappedPush.__ziven_aura_original_v3__ = originalPush;
  history.pushState = wrappedPush;
  const currentReplace = history.replaceState;
  const originalReplace = currentReplace.__ziven_aura_original_v3__ || currentReplace;
  const wrappedReplace = function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    originalReplace.apply(this, args);
    window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
    window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
  };
  wrappedReplace.__ziven_aura_original_v3__ = originalReplace;
  history.replaceState = wrappedReplace;

  // 初始化 currentPath (页面首次加载时)
  window.__ziven_aura_current_path__ = window.location.pathname;
}

// === 监听事件 ===
function setupObserver() {
  setTimeout(injectDesktopSidebar, 100);

  // 监听 mithril redraw
  extend('flarum/common/Component', 'onupdate', function () {
    if (window.matchMedia('(min-width: 992px)').matches && !document.querySelector('.App-sidebar-aura')) {
      injectDesktopSidebar();
    }
  });

  // 监听 viewport 切换 (modifyDrawer.js 触发)
  window.addEventListener('ziven-aura-viewport-changed', () => {
    // 重新评估 sidebar 注入
    injectDesktopSidebar();
  });

  // v0.2.8 改回: 监听 mithril 路由变化, 重新 render nav items
  patchHistory();
  window.addEventListener('ziven-aura-route-changed', refreshNavActive);
  window.addEventListener('popstate', refreshNavActive);
}
export default function modifySidebar() {
  setupObserver();
}